import { Fragment, useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { useParams } from "react-router-dom";

import { GoogleTagManager } from "../../shared/components/Meta/GoogleTagManager";
import { useHttpClient } from "../../shared/hooks/httpHook";
import { LoadingSpinnerFullScreenGear } from "../../shared/components/UIElements/LoadingSpinnerFullScreenGear";
import { ModalError } from "../../shared/components/UIElements/ModalError";
import { WebsiteSimForm } from "../components/WebsiteSimForm";
import { postFbCustomConversionEvent } from "../../general/util/postFbCustomConversionEvent";
import {
  getWebsiteTemplate,
  getWebsiteTemplateBySlug,
} from "../api/websiteAPI";
import { getThemeHexColor } from "../util/getThemeHexColor";
import { getIsMongoDBId } from "../util/getIsMongoDBId";
import { WebsiteData } from "../../shared/data/types";

import "react-responsive-carousel/lib/styles/carousel.min.css";
import "swiper/swiper.min.css";
import "swiper/modules/pagination/pagination.min.css";
import "swiper/modules/navigation/navigation.min.css";
import "swiper/modules/autoplay/autoplay.min.css";

import "./WebsiteStyles.css";
import "../../proposal/pages/ProposalFinalDigitalStyles.css";
import "../../general/pages/unauthenticated/AboutStyles.css";
import "../../styles/components/card-showcase.scss";
import "../../styles/components/card.scss";
import "../../styles/components/buttons.scss";

interface SimulatorProps {}

export default function Simulator(props: SimulatorProps) {
  const colors = getThemeHexColor("#043a50");
  const uid = useParams<{ id: string }>().id;
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [loadedWebsite, setLoadedWebsite] = useState<WebsiteData>(null);
  const { error, success, sendRequest, clearSuccess, clearError } =
    useHttpClient();

  useEffect(() => {
    if (window.innerWidth < 760) {
      setIsMobile(true);
    }
  }, []);

  useEffect(() => {
    if (uid) {
      if (getIsMongoDBId(uid)) {
        getWebsiteTemplate({ sendRequest, uid, setLoadedWebsite });
      } else {
        getWebsiteTemplateBySlug({ sendRequest, slug: uid, setLoadedWebsite });
      }
    }
  }, [sendRequest, uid]);

  useEffect(() => {
    if (!!loadedWebsite?.webAnalytics?.pixelFb) {
      postFbCustomConversionEvent({ id: loadedWebsite?.webAnalytics?.pixelFb });
    }
  }, [loadedWebsite?.webAnalytics?.pixelFb]);

  if (!loadedWebsite) {
    return (
      <Fragment>
        <ModalError error={error} onClear={clearError} />
        <LoadingSpinnerFullScreenGear />
      </Fragment>
    );
  }

  return (
    <Fragment>
      <Helmet>
        <link
          rel="icon"
          href={`${process.env.REACT_APP_ASSET_URL}/${loadedWebsite.companyData?.faviconUrl}`}
        />
        <title>{loadedWebsite.companyData?.name}</title>
        <meta name="description" content={loadedWebsite.companyData?.name} />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:type" content="website" />
        <meta
          property="og:image"
          content={`${process.env.REACT_APP_ASSET_URL}/${loadedWebsite.companyData?.logoUrl}`}
        />
        <meta property="og:title" content={loadedWebsite.companyData?.name} />
        <meta
          property="og:description"
          content={loadedWebsite.companyData?.name}
        />
      </Helmet>
      {!!loadedWebsite?.webAnalytics?.tagGmt && (
        <GoogleTagManager tagId={loadedWebsite.webAnalytics.tagGmt} />
      )}
      <ModalError error={error} onClear={clearError} />
      {loadedWebsite && (
        <div className="website" style={{ paddingBottom: "50px" }}>
          <WebsiteSimForm
            loadedWebsite={loadedWebsite}
            isMobile={isMobile}
            id={loadedWebsite.user}
          />
        </div>
      )}
    </Fragment>
  );
}
